import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useWallpaper, WALLPAPERS } from '../context/WallpaperContext';
import { useCategoryFilter } from '../context/CategoryFilterContext';
import { useActivity } from '../hooks/useActivity';
import { visibleTree, canAccessPage, firstAllowedPath, PAGE_GROUPS } from '../utils/pages';
import type { PageDef, PageGroup } from '../utils/pages';
import { ConfirmModal } from './ui';
import { TwoStepModal } from './TwoStepSecurity';
import RestoreGate from './RestoreGate';
import { getClientDb } from '../firebase/clientApp';
import { pruneActivityLog } from '../utils/activity';
import appLogo from '../assets/icon-noBG.png';
import type { ReactNode } from 'react';

// ── SVG Icons ──────────────────────────────────────────────────────────────

function IconDashboard() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
    </svg>
  );
}

function IconBox() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5L1.5 5v6L8 14.5 14.5 11V5L8 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M1.5 5L8 8.5 14.5 5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 8.5V14.5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function IconMerge() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 2.5v4c0 2 1.5 3.5 3.5 3.5H10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M13 2.5v4c0 2-1.5 3.5-3.5 3.5H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M8 8l2 2-2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconMemos() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="2" width="11" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.3 5.5h5.4M5.3 8h5.4M5.3 10.5h3.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function IconTrending() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M1.5 11.5l4-4 3 3 5.5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 4.5H14v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCart() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M1 1.5h2l1.5 7.5h7l1.5-5H4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="6.5" cy="12.5" r="1" fill="currentColor" />
      <circle cx="11.5" cy="12.5" r="1" fill="currentColor" />
    </svg>
  );
}

function IconAP() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="1.5" width="12" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 5.5h6M5 8h6M5 10.5h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function IconAR() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="1.5" width="12" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 5.5h6M5 8h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M5.5 11.5l2-2 1.5 1.5 2.5-2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconBrokers() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.3 10.7L10.7 5.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="5.9" cy="5.9" r="1" fill="currentColor" />
      <circle cx="10.1" cy="10.1" r="1" fill="currentColor" />
    </svg>
  );
}

function IconBank() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="6.5" width="13" height="7" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.5 6.5l6.5-4 6.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M4 9.5v2.5M8 9.5v2.5M12 9.5v2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2.2c-2.2 0-3.6 1.6-3.6 4v2.2L3 10.6h10L11.6 8.4V6.2c0-2.4-1.4-4-3.6-4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M6.5 12.7a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconProfit() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5v7M10.2 6.2c0-.9-.98-1.7-2.2-1.7-1.22 0-2.2.76-2.2 1.7 0 .94.98 1.3 2.2 1.5 1.22.2 2.2.56 2.2 1.5 0 .94-.98 1.7-2.2 1.7-1.22 0-2.2-.8-2.2-1.7"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconEntity() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 13c0-2.21 2.686-4 6-4s6 1.79 6 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconDemand() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="2.5" width="10" height="11.5" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6 2.5h4V4H6z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M5.6 7.2l1.1 1.1 2-2M5.6 11h4.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconEntityHistory() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="4.8" r="2.4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.5 12.5c0-1.9 2-3.4 4.5-3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="11.5" cy="11" r="3.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M11.5 9.6V11l1 .7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconActivity() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5V8l2.5 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconReports() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.5 11V7.5M8 11V5M11.5 11V8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconHelp() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M9.6 9.3a2.5 2.5 0 014.8.9c0 1.7-2.4 2.2-2.4 3.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="17.2" r="1.1" fill="currentColor" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path d="M6 2H3a1 1 0 00-1 1v9a1 1 0 001 1h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M10 10.5l3-3-3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 7.5H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconSun() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.42 1.42M11.54 11.54l1.41 1.41M3.05 12.95l1.42-1.41M11.54 4.46l1.41-1.41"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconMoon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M13.5 9.5A6 6 0 116.5 2.5a5 5 0 007 7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

// ── Nav sections ────────────────────────────────────────────────────────────
// Labels/paths/grouping come from utils/pages.ts's PAGE_GROUPS (the same
// registry the route guard and Settings' Team tab checkboxes use) so the
// three never drift out of sync — this file only maps each page key to its
// icon.

const NAV_ICONS: Record<string, ReactNode> = {
  dashboard:         <IconDashboard />,
  'demand-list':     <IconDemand />,
  entity:            <IconEntity />,
  'entity-history':  <IconEntityHistory />,
  reminders:         <IconBell />,
  reports:           <IconReports />,
  'activity-log':    <IconActivity />,
  purchase:          <IconCart />,
  inventory:         <IconBox />,
  memos:             <IconMemos />,
  'inventory-merge': <IconMerge />,
  sales:             <IconTrending />,
  ap:                <IconAP />,
  ar:                <IconAR />,
  brokers:           <IconBrokers />,
  bank:              <IconBank />,
  profit:            <IconProfit />,
  accounting:        <IconProfit />,   // module-parent (see PAGE_GROUPS "Accounting")
};

/** Where a path sits in the menu; the header uses its icon for the tile. */
function headerPlaceFor(pathname: string): { group: string; module?: string; icon?: ReactNode } | null {
  if (pathname === '/help') return { group: 'Help', icon: <IconHelp /> };
  for (const g of PAGE_GROUPS) {
    for (const p of g.pages) {
      if (p.children) {
        const c = p.children.find(ch => ch.path === pathname);
        if (c) return { group: g.label, module: p.label, icon: NAV_ICONS[c.key] ?? NAV_ICONS[p.key] };
      } else if (p.path === pathname) {
        return { group: g.label, icon: p.key === 'settings' ? <IconSettings /> : NAV_ICONS[p.key] };
      }
    }
  }
  return null;
}

function IconChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Rail model ─────────────────────────────────────────────────────────────
// The sidebar is a 60px icon rail + a docked drawer. Each "rail module" is
// either a PageDef that has children (drawer lists those children), or a
// synthesized bucket for a group's standalone childless pages (e.g. "Main" =
// Dashboard/Entity/Reminders). A synth bucket with exactly one page collapses
// to that page's own label and navigates on click.
interface RailModule {
  key: string;
  label: string;
  iconKey: string;
  group: string;
  leaves: PageDef[];
  singleLeaf: boolean;
}

function buildRailModules(navTree: PageGroup[]): RailModule[] {
  const mods: RailModule[] = [];
  for (const group of navTree) {
    let synth: RailModule | null = null;
    for (const p of group.pages) {
      if (p.children && p.children.length) {
        mods.push({ key: p.key, label: p.label, iconKey: p.key, group: group.label, leaves: p.children, singleLeaf: false });
      } else {
        if (!synth) {
          synth = { key: `grp:${group.label}`, label: group.label, iconKey: p.key, group: group.label, leaves: [], singleLeaf: false };
          mods.push(synth);
        }
        synth.leaves.push(p);
      }
    }
  }
  for (const m of mods) {
    if (m.key.startsWith('grp:') && m.leaves.length === 1) {
      m.label = m.leaves[0].label;
      m.singleLeaf = true;
    }
  }
  return mods;
}

// A single drawer row — flat NavLink (or a disabled "Soon" placeholder).
function DrawerLeaf({ page, onNavigate }: { page: PageDef; onNavigate: () => void }) {
  if (page.badge === 'soon') {
    return (
      <span className="sidebar-nav-item disabled">
        {page.label}
        <span className="sidebar-coming-soon">Soon</span>
      </span>
    );
  }
  return (
    <NavLink
      to={page.path}
      end
      className={({ isActive }) => `sidebar-nav-item${isActive ? ' active' : ''}`}
      onClick={onNavigate}
    >
      {page.label}
    </NavLink>
  );
}

// A module's full leaf list — ordinary pages, then the "Accountant" sub-panel
// if any leaf is accountantOnly. Shared by the docked drawer and the hover
// flyout below so the two never drift out of sync.
function ModuleLeavesList({ leaves, onNavigate }: { leaves: PageDef[]; onNavigate: () => void }) {
  return (
    <>
      {leaves.filter(l => !l.accountantOnly).map(l => (
        <DrawerLeaf key={l.key} page={l} onNavigate={onNavigate} />
      ))}
      {leaves.some(l => l.accountantOnly) && (
        <div className="app-drawer-acct">
          <div className="app-drawer-acct-head">
            <span className="app-drawer-acct-title">Accountant</span>
            <span className="app-drawer-acct-hint">owner only</span>
          </div>
          {leaves.filter(l => l.accountantOnly).map(l => (
            <DrawerLeaf key={l.key} page={l} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </>
  );
}

// Hover-capable (mouse) pointer check — gates the rail's hover flyout off on
// touch devices, where mouseenter/mouseleave don't map to a real hover intent
// and the mobile sidenav is already a fully expanded off-canvas drawer.
function supportsHover(): boolean {
  try { return window.matchMedia('(hover: hover) and (pointer: fine)').matches; }
  catch { return false; }
}

// ── Component ───────────────────────────────────────────────────────────────

interface AppLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  /** Rendered inline on the same line as the title (e.g. an "Add X" button). */
  headerActions?: ReactNode;
  /** A list page's search / filters, placed in the header bar between the
   *  title and headerActions, so the page needs no separate filter card. */
  toolbar?: ReactNode;
  /** Renders as a full-height right rail, matching the sidebar's background — for master/detail pages. */
  rightPanel?: ReactNode;
}

export default function AppLayout({ children, title, subtitle, headerActions, toolbar, rightPanel }: AppLayoutProps) {
  const { user, business, logout } = useAuth();

  // Activity Log keeps 3 months: the owner's app clears older lines once a day.
  useEffect(() => {
    if (user?.role !== 'owner' || !business) return;
    const db = getClientDb();
    if (db) pruneActivityLog(db).catch(err => console.error('Activity Log cleanup failed:', err));
  }, [user?.role, business]);
  const { theme, toggleTheme } = useTheme();
  const { categoryFilter, toggleCategoryFilter } = useCategoryFilter();
  const { wallpaper } = useWallpaper();
  const { showWarning, secondsLeft, stayLoggedIn } = useActivity();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [securityOpen,  setSecurityOpen]  = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const closeMobile = () => setMobileNavOpen(false);

  // ── Rail + drawer nav ────────────────────────────────────────────────────
  const railModules = useMemo(() => buildRailModules(visibleTree(user)), [user]);

  // Which rail module owns the current route.
  const activeModuleKey = useMemo(() => {
    for (const m of railModules)
      for (const l of m.leaves)
        if (l.path === location.pathname) return m.key;
    return null;
  }, [railModules, location.pathname]);

  // Drawer open/collapsed — personal, per-browser (same lazy-init + persist
  // pattern as ThemeContext / Customize Columns).
  const [drawerOpen, setDrawerOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('sidebar-drawer-open') !== '0'; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('sidebar-drawer-open', drawerOpen ? '1' : '0'); } catch { /* ignore */ }
    document.documentElement.style.setProperty('--sidebar-w', drawerOpen ? '268px' : '60px');
  }, [drawerOpen]);

  // A module the user opened in the rail without navigating yet — cleared the
  // moment the route actually changes. React's sanctioned "adjust state when a
  // prop changes during render" pattern (guarded setState, no effect).
  const [peekKey, setPeekKey] = useState<string | null>(null);
  const [peekPath, setPeekPath] = useState(location.pathname);
  if (peekPath !== location.pathname) {
    setPeekPath(location.pathname);
    if (peekKey) setPeekKey(null);
  }

  const openModuleKey = peekKey ?? activeModuleKey ?? railModules[0]?.key ?? '';
  const openModule = railModules.find(m => m.key === openModuleKey) ?? null;

  // Hover flyout — only meaningful while the drawer is collapsed (rail-only,
  // 60px): that's the state where a module's sub-pages aren't visible at all
  // without clicking. When the drawer is already docked open, its own content
  // already sits right where a flyout would appear, so hover does nothing extra.
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState<{ top: number; left: number } | null>(null);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function cancelHoverClose() {
    if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; }
  }
  function scheduleHoverClose() {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => setHoverKey(null), 150);
  }
  function onRailHoverEnter(m: RailModule, el: HTMLElement) {
    if (drawerOpen || m.singleLeaf || !supportsHover()) return;
    cancelHoverClose();
    const rect = el.getBoundingClientRect();
    setHoverPos({ top: Math.max(8, Math.min(rect.top, window.innerHeight - 260)), left: rect.right + 8 });
    setHoverKey(m.key);
  }
  function onRailHoverLeave(m: RailModule) {
    if (m.singleLeaf) return;
    scheduleHoverClose();
  }
  useEffect(() => () => cancelHoverClose(), []);

  const hoverModule = hoverKey ? railModules.find(m => m.key === hoverKey) ?? null : null;

  function onRailClick(m: RailModule) {
    if (m.singleLeaf) {
      navigate(m.leaves[0].path);
      setPeekKey(null);
      setDrawerOpen(true);
      closeMobile();
      return;
    }
    if (openModuleKey === m.key) {
      setDrawerOpen(o => !o);
    } else {
      setPeekKey(m.key === activeModuleKey ? null : m.key);
      setDrawerOpen(true);
    }
  }

  const wallpaperSrc = WALLPAPERS.find(w => w.id === wallpaper)?.src;

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const fmtSeconds = (s: number) => {
    const m = Math.floor(Math.max(s, 0) / 60);
    const sec = Math.max(s, 0) % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="app-shell">
      <RestoreGate />
      {/* ── Inactivity warning — full viewport (left:0 to cover sidebar) ── */}
      {showWarning && (
        <div className="modal-overlay" style={{ zIndex: 200, left: 0 }}>
          <div className="modal" style={{ maxWidth: 400, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏱</div>
            <h3 style={{ marginBottom: 8 }}>Still there?</h3>
            <p style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 20 }}>
              You'll be signed out in <strong style={{ color: 'var(--danger)' }}>{fmtSeconds(secondsLeft)}</strong> due to inactivity.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={logout}>Sign Out Now</button>
              <button className="btn btn-primary" style={{ width: 'auto' }} onClick={stayLoggedIn}>
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile top bar — hamburger + logo, hidden on desktop ── */}
      <div className="mobile-topbar">
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open menu"
        >
          <IconMenu />
        </button>
        <div className="sidebar-logo-icon" style={{ width: 28, height: 28 }}><img src={appLogo} alt="" /></div>
        <div className="mobile-topbar-title">{business?.businessName ?? 'DiaWolt'}</div>
      </div>

      {/* ── Mobile drawer backdrop ── */}
      {mobileNavOpen && (
        <div className="sidebar-backdrop" onClick={closeMobile} />
      )}

      {/* ── Icon rail + docked drawer ── */}
      <div className={`app-sidenav${mobileNavOpen ? ' open' : ''}`}>
        <nav className="app-rail" aria-label="Sections">
          {/* The logo goes home: Dashboard, or the first page a staff login can open. */}
          <button type="button" className="app-rail-logo" title="Dashboard" aria-label="Go to Dashboard"
            onClick={() => { const to = canAccessPage(user, 'dashboard') ? '/dashboard' : firstAllowedPath(user); if (to) { closeMobile(); navigate(to); } }}>
            <img src={appLogo} alt="" />
          </button>

          <div className="app-rail-mods">
            {railModules.map((m, i) => (
              <Fragment key={m.key}>
                {i > 0 && railModules[i - 1].group !== m.group && <span className="app-rail-divider" />}
                <button
                  type="button"
                  className={`app-rail-btn${m.key === activeModuleKey ? ' on' : ''}${m.key === openModuleKey && drawerOpen && m.key !== activeModuleKey ? ' peek' : ''}`}
                  title={m.label}
                  aria-label={m.label}
                  aria-current={m.key === activeModuleKey ? 'page' : undefined}
                  onClick={() => onRailClick(m)}
                  onMouseEnter={e => onRailHoverEnter(m, e.currentTarget)}
                  onMouseLeave={() => onRailHoverLeave(m)}
                >
                  <span className="app-rail-ic">{NAV_ICONS[m.iconKey]}</span>
                </button>
              </Fragment>
            ))}
            {/* Help: last in the menu, under Accounting. Open to every login. */}
            {railModules.length > 0 && <span className="app-rail-divider" />}
            <NavLink
              to="/help"
              end
              className={({ isActive }) => `app-rail-btn${isActive ? ' on' : ''}`}
              title="Help"
              aria-label="Help"
              onClick={closeMobile}
            >
              <span className="app-rail-ic"><IconHelp /></span>
            </NavLink>
          </div>

          <div className="app-rail-foot">
            <button
              type="button"
              className="app-rail-btn"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              aria-label="Toggle theme"
              aria-pressed={theme === 'dark'}
            >
              {theme === 'dark' ? <IconMoon /> : <IconSun />}
            </button>
            {canAccessPage(user, 'settings') && (
              <NavLink
                to="/settings"
                end
                className={({ isActive }) => `app-rail-btn${isActive ? ' on' : ''}`}
                title="Settings"
                aria-label="Settings"
                onClick={closeMobile}
              >
                <IconSettings />
              </NavLink>
            )}
            <button
              type="button"
              className={`rail-switch${categoryFilter === 'books' ? ' on' : ''}`}
              onClick={toggleCategoryFilter}
              title={categoryFilter === 'books' ? 'Books — click for Real' : 'Real — click for Books'}
              aria-label="Toggle Real / Books across Purchase, Inventory and Sales"
              role="switch"
              aria-checked={categoryFilter === 'books'}
            >
              <span className="rail-switch-knob" />
            </button>
            <button type="button" className="app-rail-btn" onClick={() => setConfirmLogout(true)} title="Sign out" aria-label="Sign out">
              <IconLogout />
            </button>
            {/* Avatar opens Sign in security (authenticator app two step), a
                shortcut to the same panel as Settings › Security. */}
            <button type="button" className="app-rail-avatar" onClick={() => setSecurityOpen(true)}
              title={`${user?.name ?? ''}${user?.role ? ` · ${user.role}` : ''} · Sign in security`}
              aria-label="Sign in security"
              style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
              {initials}
            </button>
          </div>
        </nav>

        <div className={`app-drawer${drawerOpen ? '' : ' closed'}`}>
          {openModule && (
            <>
              <div className="app-drawer-head">
                <span className="app-drawer-title">{openModule.label}</span>
                <button
                  type="button"
                  className="app-drawer-collapse"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Collapse menu"
                  title="Collapse"
                >
                  <IconChevronLeft />
                </button>
              </div>
              <div className="app-drawer-scroll">
                <ModuleLeavesList leaves={openModule.leaves} onNavigate={closeMobile} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Rail hover flyout — collapsed-rail-only quick nav preview ── */}
      {hoverModule && hoverPos && !drawerOpen && createPortal(
        <div
          className="app-rail-flyout"
          style={{ position: 'fixed', top: hoverPos.top, left: hoverPos.left }}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={() => scheduleHoverClose()}
        >
          <div className="app-rail-flyout-title">{hoverModule.label}</div>
          <ModuleLeavesList leaves={hoverModule.leaves} onNavigate={() => setHoverKey(null)} />
        </div>,
        document.body,
      )}

      {/* ── Main ── */}
      <main
        className={`app-main${wallpaperSrc ? ' has-wallpaper' : ''}`}
        style={wallpaperSrc ? {
          backgroundImage: `url(${wallpaperSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        } : undefined}
      >
        <div className="page-content">
          {/* One slim sticky bar: title, then the page's own toolbar, then
              its header buttons. The subtitle prop is no longer shown. */}
          {(title || subtitle || headerActions || toolbar) && (
            <div className="page-header">
              <div className="page-header-gem" aria-hidden="true" />
              <div className="page-header-titlerow">
                {title && (() => {
                  const place = headerPlaceFor(location.pathname);
                  return (
                    <div className="page-title-wrap">
                      {place?.icon && <span className="page-title-icon" aria-hidden="true">{place.icon}</span>}
                      <div className="page-title-text">
                        <h1 className="page-title">
                          {title}
                        </h1>
                      </div>
                    </div>
                  );
                })()}
                {toolbar && <div className="page-header-toolbar no-print">{toolbar}</div>}
                {headerActions && <div className="page-header-actions">{headerActions}</div>}
              </div>
            </div>
          )}
          {children}
        </div>
      </main>

      {/* ── Right rail — full-height, matches sidebar background ── */}
      {rightPanel && (
        <aside className="app-right-panel">{rightPanel}</aside>
      )}

      <TwoStepModal open={securityOpen} onClose={() => setSecurityOpen(false)} />

      <ConfirmModal
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        onConfirm={logout}
        title="Sign out?"
        message="You'll need to sign in again to get back into the app."
        confirmLabel="Sign Out"
      />
    </div>
  );
}
